import { useState, useMemo, useEffect } from 'react'
import {
  Drawer,
  Table,
  Input,
  Switch,
  Button,
  Space,
  App,
  Dropdown,
  Spin,
  type TableColumnsType,
  type MenuProps
} from 'antd'
import {
  EditOutlined,
  UndoOutlined,
  SearchOutlined,
  DownloadOutlined,
  MenuOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useFieldLabels } from '../../hooks'
import { labelService } from '../../services/labelService'
import { metadataService } from '../../services/metadataService'
import { errorService } from '../../services/errorService'
import type { UpdateLabelParams, FieldConfiguration } from '../../types/labels.types'
import type { ApiResponse } from '../../types/api.types'

// Extended params for internal tracking
interface UpdateLabelParamsWithMeta extends UpdateLabelParams {
  _isBulkOperation?: boolean
}

interface DeleteLabelParamsWithMeta {
  uid: string
  fieldName: string
  _isBulkOperation?: boolean
}

interface FieldConfigRow {
  fieldName: string
  label: string
  defaultLabel: string
  isCustom: boolean
  visible: boolean
  dataType?: string
  uid?: string | null
  version: number
}

interface FieldConfigurationDrawerProps {
  open: boolean
  onClose: () => void
  context: string
  contextDisplayName?: string
}

export function FieldConfigurationDrawer({
  open,
  onClose,
  context,
  contextDisplayName
}: FieldConfigurationDrawerProps) {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const { data: labels, isLoading: labelsLoading } = useFieldLabels(context)

  // Fetch metadata to check is_visible property
  // Convert context to singular form for metadata lookup (offerings → offering)
  const metadataModel = context.replace(/s$/, '') // Remove trailing 's'

  const { data: metadata, isLoading: metadataLoading } = useQuery({
    queryKey: ['field-metadata', metadataModel],
    queryFn: async () => {
      const response = await metadataService.getNonSystemFields(metadataModel)
      if (!response.success || !response.data) {
        throw new Error(`Failed to fetch metadata for ${context}`)
      }
      return response.data.fields
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - metadata rarely changes
  })

  const isLoading = labelsLoading || metadataLoading

  const [searchText, setSearchText] = useState<string>('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set())
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = localStorage.getItem('fieldConfigDrawerPageSize')
    return saved ? parseInt(saved, 10) : 10
  })

  // Save page size to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fieldConfigDrawerPageSize', pageSize.toString())
  }, [pageSize])

  // Debug: Log metadata structure when loaded
  useEffect(() => {
    if (metadata) {
      console.debug('[FieldConfigurationDrawer] Metadata loaded:', {
        model: metadataModel,
        fieldCount: Object.keys(metadata).length,
        sampleFields: Object.entries(metadata).slice(0, 3).map(([key, field]) => ({
          fieldName: key,
          py_type: field.py_type,
          title: field.title,
          is_visible: field.is_visible
        }))
      })
    }
  }, [metadata, metadataModel])

  // Track individual bulk action loading states
  const [bulkActionLoading, setBulkActionLoading] = useState<{
    hideSelected: boolean
    showSelected: boolean
    resetSelected: boolean
    showAll: boolean
  }>({
    hideSelected: false,
    showSelected: false,
    resetSelected: false,
    showAll: false
  })

  // Update label mutation
  const updateLabelMutation = useMutation<
    ApiResponse<FieldConfiguration>,
    Error,
    UpdateLabelParamsWithMeta
  >({
    mutationFn: (params: UpdateLabelParamsWithMeta) => {
      // Remove internal meta field before calling service
      const { _isBulkOperation, ...serviceParams } = params
      return labelService.updateLabel(serviceParams)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })
      // Only show individual success message if not a bulk operation
      if (!variables._isBulkOperation) {
        message.success('Label updated successfully')
      }
      // Remove loading state for this field
      setLoadingFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(variables.fieldName)
        return newSet
      })
    },
    onError: (error: any, variables) => {
      errorService.showError(
        'Failed to Update Label',
        'An error occurred while updating the field label',
        [{ code: 'UPDATE_FAILED', message: error?.message || String(error) }]
      )
      // Remove loading state for this field
      setLoadingFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(variables.fieldName)
        return newSet
      })
    }
  })

  // Delete label mutation (reset to default)
  const deleteLabelMutation = useMutation<
    ApiResponse<void>,
    Error,
    DeleteLabelParamsWithMeta
  >({
    mutationFn: ({ uid }: DeleteLabelParamsWithMeta) =>
      labelService.deleteLabel(uid),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })
      // Only show individual success message if not a bulk operation
      if (!variables._isBulkOperation) {
        message.success('Label reset to default')
      }
      // Remove loading state for this field
      setLoadingFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(variables.fieldName)
        return newSet
      })
    },
    onError: (error: any, variables) => {
      errorService.showError(
        'Failed to Reset Label',
        'An error occurred while resetting the field label to default',
        [{ code: 'RESET_FAILED', message: error?.message || String(error) }]
      )
      // Remove loading state for this field
      setLoadingFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(variables.fieldName)
        return newSet
      })
    }
  })

  // Transform labels data into table rows, filtering by metadata is_visible
  const dataSource = useMemo(() => {
    if (!labels || !metadata) return []

    const rows = Object.keys(labels.labels)
      .filter((fieldName) => {
        // Filter out fields where metadata.is_visible = false
        // These are system fields like uid, createdAt, deletedAt, etc.
        const fieldMeta = metadata[fieldName]
        if (!fieldMeta) return true // Include if metadata not found (shouldn't happen)
        return fieldMeta.is_visible !== false // Default to true if not specified
      })
      .map((fieldName) => ({
        fieldName,
        label: labels.labels[fieldName],
        defaultLabel: metadata[fieldName]?.title || fieldName, // Get from metadata.title
        isCustom: labels.isCustom[fieldName],
        visible: labels.visibility[fieldName], // This is rp_visible (user toggle)
        dataType: metadata[fieldName]?.py_type || 'text',
        uid: labels.uids[fieldName], // UID of existing configuration (null if using default)
        version: labels.versions[fieldName] // Current version for optimistic locking
      }))

    // Debug: Log data types distribution
    const typeDistribution = rows.reduce((acc, row) => {
      acc[row.dataType || 'unknown'] = (acc[row.dataType || 'unknown'] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    console.debug('[FieldConfigurationDrawer] Data type distribution:', typeDistribution)

    return rows
  }, [labels, metadata])

  // Filter by search text only
  // Note: dataSource already filtered by metadata.is_visible (system fields like uid, createdAt excluded)
  // The 'visible' column shown in table is rp_visible (user-controlled toggle for data mapping)
  const filteredData = useMemo(() => {
    if (!searchText) return dataSource

    const lowerSearch = searchText.toLowerCase()
    return dataSource.filter(
      (row) =>
        row.fieldName.toLowerCase().includes(lowerSearch) ||
        row.label.toLowerCase().includes(lowerSearch)
    )
  }, [dataSource, searchText])

  // Handle label save
  const handleSave = async (fieldName: string, newLabel: string) => {
    const row = dataSource.find((r) => r.fieldName === fieldName)
    if (!row) return

    // Set loading state
    setLoadingFields(prev => new Set(prev).add(fieldName))

    await updateLabelMutation.mutateAsync({
      uid: row.uid,
      context,
      fieldName,
      label: newLabel,
      visible: row.visible,
      version: row.version
    })

    setEditingField(null)
  }

  // Handle visibility toggle
  const handleVisibilityToggle = async (fieldName: string, visible: boolean) => {
    const row = dataSource.find((r) => r.fieldName === fieldName)
    if (!row) return

    // Set loading state
    setLoadingFields(prev => new Set(prev).add(fieldName))

    await updateLabelMutation.mutateAsync({
      uid: row.uid,
      context,
      fieldName,
      label: row.label,
      visible,
      version: row.version
    })
  }

  // Handle reset to default
  const handleReset = async (fieldName: string) => {
    const row = dataSource.find((r) => r.fieldName === fieldName)
    if (!row || !row.uid) {
      errorService.showError(
        'Cannot Reset Label',
        'No custom configuration found for this field',
        [{ code: 'NO_CUSTOM_CONFIG', message: 'This field is using the default label' }]
      )
      return
    }

    // Set loading state
    setLoadingFields(prev => new Set(prev).add(fieldName))

    await deleteLabelMutation.mutateAsync({ uid: row.uid, fieldName })
  }

  // Bulk operations
  const handleBulkHide = async () => {
    // Hide all selected fields that are currently visible (skip already hidden)
    const selectedFields = dataSource.filter((f) =>
      selectedRowKeys.includes(f.fieldName) && f.visible
    )

    if (selectedFields.length === 0) {
      message.warning('No visible fields selected to hide')
      return
    }

    setBulkActionLoading(prev => ({ ...prev, hideSelected: true }))
    try {
      // Build bulk upsert request (works for fields with or without existing configs)
      const items = selectedFields.map(f => {
        const item: any = {
          context,
          fieldName: f.fieldName,
          configType: 'label',
          value: {
            label: f.label,
            visible: false
          }
        }
        // Include uid and version only if they exist (for updates)
        if (f.uid) {
          item.uid = f.uid
          item.version = f.version
        }
        return item
      })

      // Call bulk upsert endpoint (creates new configs for fields without UIDs)
      const response = await labelService.bulkUpsert(items)

      if (!response.success || !response.data) {
        throw new Error('Bulk update failed')
      }

      // Check for partial failures (backend returns code: 0 for success, 1+ for errors)
      const failures = response.data.filter(item => item.code !== 0)
      if (failures.length > 0) {
        errorService.showError(
          'Partial Failure',
          `${failures.length} field(s) failed to hide`,
          failures.map(f => ({
            code: `ERROR_CODE_${f.code}`,
            message: f.detail || 'Unknown error',
            details: { uid: f.uid }
          }))
        )
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })

      const successCount = response.data.filter(r => r.code === 0).length
      message.success(`${successCount} field${successCount === 1 ? '' : 's'} hidden successfully`)
      setSelectedRowKeys([])
    } catch (error: any) {
      errorService.showError(
        'Failed to Hide Fields',
        'An error occurred while hiding the selected fields',
        [{ code: 'BULK_HIDE_FAILED', message: error?.message || String(error) }]
      )
    } finally {
      setBulkActionLoading(prev => ({ ...prev, hideSelected: false }))
    }
  }

  const handleBulkShow = async () => {
    // Show all selected fields that are currently hidden (skip already visible)
    const selectedFields = dataSource.filter((f) =>
      selectedRowKeys.includes(f.fieldName) && !f.visible
    )

    if (selectedFields.length === 0) {
      message.warning('No hidden fields selected to show')
      return
    }

    setBulkActionLoading(prev => ({ ...prev, showSelected: true }))
    try {
      // Build bulk upsert request (works for fields with or without existing configs)
      const items = selectedFields.map(f => {
        const item: any = {
          context,
          fieldName: f.fieldName,
          configType: 'label',
          value: {
            label: f.label,
            visible: true
          }
        }
        // Include uid and version only if they exist (for updates)
        if (f.uid) {
          item.uid = f.uid
          item.version = f.version
        }
        return item
      })

      // Call bulk upsert endpoint (creates new configs for fields without UIDs)
      const response = await labelService.bulkUpsert(items)

      if (!response.success || !response.data) {
        throw new Error('Bulk update failed')
      }

      // Check for partial failures (backend returns code: 0 for success, 1+ for errors)
      const failures = response.data.filter(item => item.code !== 0)
      if (failures.length > 0) {
        errorService.showError(
          'Partial Failure',
          `${failures.length} field(s) failed to show`,
          failures.map(f => ({
            code: `ERROR_CODE_${f.code}`,
            message: f.detail || 'Unknown error',
            details: { uid: f.uid }
          }))
        )
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })

      const successCount = response.data.filter(r => r.code === 0).length
      message.success(`${successCount} field${successCount === 1 ? '' : 's'} shown successfully`)
      setSelectedRowKeys([])
    } catch (error: any) {
      errorService.showError(
        'Failed to Show Fields',
        'An error occurred while showing the selected fields',
        [{ code: 'BULK_SHOW_FAILED', message: error?.message || String(error) }]
      )
    } finally {
      setBulkActionLoading(prev => ({ ...prev, showSelected: false }))
    }
  }

  const handleBulkReset = async () => {
    // Only reset fields where the reset button would show:
    // Has custom config AND (visibility is OFF OR label differs from default)
    const selectedFields = dataSource.filter((f) =>
      selectedRowKeys.includes(f.fieldName) &&
      f.isCustom &&
      f.uid &&
      (!f.visible || f.label !== f.defaultLabel)
    )

    if (selectedFields.length === 0) {
      message.warning('No fields with custom configurations to reset')
      return
    }

    setBulkActionLoading(prev => ({ ...prev, resetSelected: true }))
    try {
      // Use bulk delete endpoint
      const uids = selectedFields.map(f => f.uid!)
      const response = await labelService.bulkDelete(uids)

      if (!response.success || !response.data) {
        throw new Error('Bulk delete failed')
      }

      // Check for partial failures (backend returns code: 0 for success, 1+ for errors)
      const failures = response.data.filter(item => item.code !== 0)
      if (failures.length > 0) {
        errorService.showError(
          'Partial Failure',
          `${failures.length} field(s) failed to reset`,
          failures.map(f => ({
            code: `ERROR_CODE_${f.code}`,
            message: f.detail || 'Unknown error',
            details: { uid: f.uid }
          }))
        )
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })

      const successCount = response.data.filter(r => r.code === 0).length
      message.success(`${successCount} label${successCount === 1 ? '' : 's'} reset to default successfully`)
      setSelectedRowKeys([])
    } catch (error: any) {
      errorService.showError(
        'Failed to Reset Labels',
        'An error occurred while resetting the selected labels to default',
        [{ code: 'BULK_RESET_FAILED', message: error?.message || String(error) }]
      )
    } finally {
      setBulkActionLoading(prev => ({ ...prev, resetSelected: false }))
    }
  }
  const handleShowAll = async () => {
    // Show all hidden fields (skip already visible)
    const hiddenFields = dataSource.filter(f => !f.visible)

    if (hiddenFields.length === 0) {
      message.info('All fields are already visible')
      return
    }

    setBulkActionLoading(prev => ({ ...prev, showAll: true }))
    try {
      // Build bulk upsert request (works for fields with or without existing configs)
      const items = hiddenFields.map(f => {
        const item: any = {
          context,
          fieldName: f.fieldName,
          configType: 'label',
          value: {
            label: f.label,
            visible: true
          }
        }
        // Include uid and version only if they exist (for updates)
        if (f.uid) {
          item.uid = f.uid
          item.version = f.version
        }
        return item
      })

      // Call bulk upsert endpoint (creates new configs for fields without UIDs)
      const response = await labelService.bulkUpsert(items)

      if (!response.success || !response.data) {
        throw new Error('Bulk update failed')
      }

      // Check for partial failures (backend returns code: 0 for success, 1+ for errors)
      const failures = response.data.filter(item => item.code !== 0)
      if (failures.length > 0) {
        errorService.showError(
          'Partial Failure',
          `${failures.length} field(s) failed to show`,
          failures.map(f => ({
            code: `ERROR_CODE_${f.code}`,
            message: f.detail || 'Unknown error',
            details: { uid: f.uid }
          }))
        )
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['field-labels', context] })

      const successCount = response.data.filter(r => r.code === 0).length
      message.success(`${successCount} field${successCount === 1 ? '' : 's'} shown successfully`)
    } catch (error: any) {
      errorService.showError(
        'Failed to Show All Fields',
        'An error occurred while showing all fields',
        [{ code: 'SHOW_ALL_FAILED', message: error?.message || String(error) }]
      )
    } finally {
      setBulkActionLoading(prev => ({ ...prev, showAll: false }))
    }
  }

  // Check if any bulk operation is currently running
  const isAnyBulkOperationRunning = Object.values(bulkActionLoading).some(loading => loading)

  // Bulk actions menu
  const bulkActionsMenu: MenuProps = {
    items: [
      {
        key: 'hide',
        label: bulkActionLoading.hideSelected ? (
          <span>
            <LoadingOutlined style={{ marginRight: 8 }} />
            Hiding Selected Fields...
          </span>
        ) : (
          'Hide Selected Fields'
        ),
        onClick: handleBulkHide,
        disabled: selectedRowKeys.length === 0 || isAnyBulkOperationRunning
      },
      {
        key: 'show',
        label: bulkActionLoading.showSelected ? (
          <span>
            <LoadingOutlined style={{ marginRight: 8 }} />
            Showing Selected Fields...
          </span>
        ) : (
          'Show Selected Fields'
        ),
        onClick: handleBulkShow,
        disabled: selectedRowKeys.length === 0 || isAnyBulkOperationRunning
      },
      {
        key: 'reset',
        label: bulkActionLoading.resetSelected ? (
          <span>
            <LoadingOutlined style={{ marginRight: 8 }} />
            Resetting Selected...
          </span>
        ) : (
          'Reset Selected to Default'
        ),
        onClick: handleBulkReset,
        disabled: selectedRowKeys.length === 0 || isAnyBulkOperationRunning
      },
      {
        key: 'show-all',
        label: bulkActionLoading.showAll ? (
          <span>
            <LoadingOutlined style={{ marginRight: 8 }} />
            Showing All Fields...
          </span>
        ) : (
          'Show All Fields'
        ),
        onClick: handleShowAll,
        disabled: isAnyBulkOperationRunning
      }
    ]
  }

  // Table columns
  const columns: TableColumnsType<FieldConfigRow> = [
    {
      title: 'Default Label',
      dataIndex: 'defaultLabel',
      key: 'defaultLabel',
      sorter: (a, b) => a.defaultLabel.localeCompare(b.defaultLabel),
      width: 200
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (text, record) => {
        if (editingField === record.fieldName) {
          return (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onPressEnter={() => handleSave(record.fieldName, editValue)}
              onBlur={() => {
                if (editValue !== record.label) {
                  handleSave(record.fieldName, editValue)
                } else {
                  setEditingField(null)
                }
              }}
              autoFocus
              style={{ width: '100%' }}
            />
          )
        }
        return (
          <span
            onClick={() => {
              setEditingField(record.fieldName)
              setEditValue(text)
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {text}
            <EditOutlined style={{ color: '#1890ff', fontSize: 12 }} />
          </span>
        )
      }
    },
    {
      title: 'Data Type',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 120,
      render: (dataType: string) => {
        // Map YAML schema types to user-friendly display names
        const typeMap: Record<string, string> = {
          // Text types
          string: 'Text',
          uuid: 'Text',
          // Numeric types
          integer: 'Number',
          numeric: 'Number',
          // Boolean
          bool: 'Boolean',
          // Date/Time types
          timestamp: 'Date',
          timestamptz: 'Date',
          date: 'Date'
        }
        return typeMap[dataType] || 'Text'
      },
      filters: [
        { text: 'Text', value: 'string' },
        { text: 'Text', value: 'uuid' },
        { text: 'Number', value: 'integer' },
        { text: 'Number', value: 'numeric' },
        { text: 'Boolean', value: 'bool' },
        { text: 'Date', value: 'timestamp' },
        { text: 'Date', value: 'timestamptz' },
        { text: 'Date', value: 'date' }
      ],
      onFilter: (value, record) => record.dataType === value
    },
    {
      title: 'Visible',
      dataIndex: 'visible',
      key: 'visible',
      width: 120,
      render: (visible: boolean, record) => (
        <Switch
          checked={visible}
          onChange={(checked) => handleVisibilityToggle(record.fieldName, checked)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      ),
      filters: [
        { text: 'Visible', value: true },
        { text: 'Hidden', value: false }
      ],
      onFilter: (value, record) => record.visible === value
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const isLoading = loadingFields.has(record.fieldName)

        if (isLoading) {
          return <Spin size="small" />
        }

        // Show reset button when there's a custom config AND (visibility is OFF OR label differs from default)
        const shouldShowReset = record.isCustom && (!record.visible || record.label !== record.defaultLabel)

        return shouldShowReset ? (
          <Button
            type="link"
            size="small"
            icon={<UndoOutlined />}
            onClick={() => handleReset(record.fieldName)}
          >
            Reset
          </Button>
        ) : null
      }
    }
  ]

  return (
    <Drawer
      title={`Configure Fields - ${contextDisplayName || context}`}
      width={1200}
      placement="right"
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Button icon={<DownloadOutlined />} disabled>
            Export to Excel
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Search and Bulk Actions */}
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder="Search fields..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Dropdown menu={bulkActionsMenu} trigger={['click']}>
            <Button icon={<MenuOutlined />}>
              Bulk Actions
            </Button>
          </Dropdown>
        </Space>

        {/* Field configuration table */}
        <Table<FieldConfigRow>
          dataSource={filteredData}
          columns={columns}
          rowKey="fieldName"
          loading={isLoading || isAnyBulkOperationRunning}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys as string[])
          }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            onShowSizeChange: (_current, size) => setPageSize(size),
            showTotal: (total) => `Total ${total} fields`
          }}
          scroll={{ y: 600 }}
        />
      </Space>
    </Drawer>
  )
}

export default FieldConfigurationDrawer
